import React, { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';
import { FileText, ArrowLeft, CheckCircle, XCircle, Eye, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DistributionRights, DistributionTrack } from '../services/DistributionService';

export const AdminDistribution: React.FC = () => {
  const { distributionService } = useData();
  const [distributionRights, setDistributionRights] = useState<DistributionRights[]>([]);
  const [distributionTracks, setDistributionTracks] = useState<DistributionTrack[]>([]);
  const [loading, setLoading] = useState({
    rights: true,
    tracks: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'rights' | 'tracks'>('rights');
  const [error, setError] = useState<string | null>(null);
  const [selectedRights, setSelectedRights] = useState<DistributionRights | null>(null);

  // Mock data for demo purposes
  const mockRights: DistributionRights[] = [
    {
      id: '1',
      user_id: 'user1',
      rights_type: 'audio',
      agreement_accepted: true,
      agreement_date: '2025-04-10T10:00:00Z',
      agreement_version: 'v1.0',
      territory_restrictions: null,
      created_at: '2025-04-10T09:30:00Z',
      updated_at: null
    },
    {
      id: '2',
      user_id: 'user2',
      rights_type: 'both',
      agreement_accepted: true,
      agreement_date: '2025-04-09T14:20:00Z',
      agreement_version: 'v1.0',
      territory_restrictions: null,
      created_at: '2025-04-09T14:00:00Z',
      updated_at: null
    },
    {
      id: '3',
      user_id: 'user3',
      rights_type: 'video',
      agreement_accepted: false,
      agreement_date: null,
      agreement_version: null,
      territory_restrictions: null,
      created_at: '2025-04-08T11:15:00Z',
      updated_at: null
    }
  ];

  const mockTracks: DistributionTrack[] = [
    {
      id: '1',
      user_id: 'user1',
      performance_id: 'perf1',
      artist_name: 'Jamie Smith',
      original_title: 'Shape of You',
      original_artist: 'Ed Sheeran',
      distribution_status: 'live',
      distributor_track_id: 'dt123',
      youtube_music_url: 'https://music.youtube.com/watch?v=123',
      youtube_video_id: 'abcd123',
      video_url: 'https://youtube.com/watch?v=abcd123',
      fuga_asset_id: 'fa123',
      revenue_to_date: 12.50,
      last_payment_date: '2025-04-01T00:00:00Z',
      payment_status: 'paid',
      rights_cleared: true,
      distribution_notes: null,
      revenue_split: {
        artist: 70,
        platform: 30
      },
      created_at: '2025-03-15T10:00:00Z',
      updated_at: '2025-04-01T10:00:00Z'
    },
    {
      id: '2',
      user_id: 'user2',
      performance_id: 'perf2',
      artist_name: 'Alice Johnson',
      original_title: 'Bohemian Rhapsody',
      original_artist: 'Queen',
      distribution_status: 'processing',
      distributor_track_id: null,
      youtube_music_url: null,
      youtube_video_id: null,
      video_url: null,
      fuga_asset_id: 'fa456',
      revenue_to_date: 0,
      last_payment_date: null,
      payment_status: 'pending',
      rights_cleared: true,
      distribution_notes: 'Processing with FUGA',
      revenue_split: {
        artist: 70,
        platform: 30
      },
      created_at: '2025-04-05T15:30:00Z',
      updated_at: null
    },
    {
      id: '3',
      user_id: 'user3',
      performance_id: 'perf3',
      artist_name: 'Miguel Rodriguez',
      original_title: 'Despacito',
      original_artist: 'Luis Fonsi',
      distribution_status: 'rejected',
      distributor_track_id: null,
      youtube_music_url: null,
      youtube_video_id: null,
      video_url: null,
      fuga_asset_id: null,
      revenue_to_date: 0,
      last_payment_date: null,
      payment_status: 'pending',
      rights_cleared: false,
      distribution_notes: 'Rights not cleared - requires original artist approval',
      revenue_split: {
        artist: 70,
        platform: 30
      },
      created_at: '2025-04-02T09:20:00Z',
      updated_at: '2025-04-03T14:15:00Z'
    }
  ];

  useEffect(() => {
    // In a real implementation, we would fetch this data from the API
    // For now, we'll just use mock data
    setDistributionRights(mockRights);
    setDistributionTracks(mockTracks);
    setLoading({
      rights: false,
      tracks: false
    });
  }, []);

  const filteredRights = distributionRights.filter(right => {
    if (!searchTerm) return true;
    return right.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
           right.rights_type.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const filteredTracks = distributionTracks.filter(track => {
    if (!searchTerm) return true;
    return track.artist_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           track.original_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
           track.original_artist.toLowerCase().includes(searchTerm.toLowerCase()) ||
           track.distribution_status.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'live':
        return 'bg-green-900/30 text-green-400';
      case 'processing':
        return 'bg-yellow-900/30 text-yellow-400';
      case 'rejected':
        return 'bg-red-900/30 text-red-400';
      case 'pending':
        return 'bg-blue-900/30 text-blue-400';
      default:
        return 'bg-kod-purple-700/50 text-white/70';
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center mb-6">
        <Link to="/admin/dashboard" className="mr-4 text-white/70 hover:text-white transition-colors">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl font-bold">Distribution Management</h1>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-500">
          {error}
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div className="flex space-x-4">
          <button
            className={`px-4 py-2 rounded-lg ${viewMode === 'rights' ? 'bg-kod-accent text-white' : 'bg-kod-purple-700 text-white/70'}`}
            onClick={() => setViewMode('rights')}
          >
            Distribution Rights
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${viewMode === 'tracks' ? 'bg-kod-accent text-white' : 'bg-kod-purple-700 text-white/70'}`}
            onClick={() => setViewMode('tracks')}
          >
            Distribution Tracks
          </button>
        </div>

        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" size={18} />
          <input
            type="text"
            placeholder={`Search ${viewMode === 'rights' ? 'rights' : 'tracks'}...`}
            className="w-full pl-10 pr-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent text-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {viewMode === 'rights' ? (
        <div className="card">
          {loading.rights ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-kod-accent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-kod-purple-600">
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">User ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Rights Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Agreement Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Agreement Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Version</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kod-purple-600/50">
                  {filteredRights.length > 0 ? (
                    filteredRights.map((right) => (
                      <tr key={right.id} className="hover:bg-kod-purple-700/50">
                        <td className="px-4 py-4 whitespace-nowrap">{right.user_id}</td>
                        <td className="px-4 py-4 whitespace-nowrap capitalize">{right.rights_type}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {right.agreement_accepted ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-900/30 text-green-400">
                              <CheckCircle size={12} className="mr-1" /> Accepted
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-900/30 text-red-400">
                              <XCircle size={12} className="mr-1" /> Not Accepted
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {right.agreement_date ? new Date(right.agreement_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">{right.agreement_version || '-'}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button 
                            className="p-1 text-white/70 hover:text-white"
                            onClick={() => setSelectedRights(right)}
                          >
                            <Eye size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-white/50">
                        No distribution rights found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          {loading.tracks ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-kod-accent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-kod-purple-600">
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Artist</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Original Artist</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Revenue</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kod-purple-600/50">
                  {filteredTracks.length > 0 ? (
                    filteredTracks.map((track) => (
                      <tr key={track.id} className="hover:bg-kod-purple-700/50">
                        <td className="px-4 py-4 whitespace-nowrap">{track.artist_name}</td>
                        <td className="px-4 py-4 whitespace-nowrap">{track.original_title}</td>
                        <td className="px-4 py-4 whitespace-nowrap">{track.original_artist}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${getStatusClass(track.distribution_status)}`}>
                            {track.distribution_status}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">${track.revenue_to_date.toFixed(2)}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {new Date(track.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-white/50">
                        No distribution tracks found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Distribution Rights Details Modal */}
      {selectedRights && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Distribution Rights Details</h2>
              <button
                onClick={() => setSelectedRights(null)}
                className="p-2 hover:bg-kod-purple-700 rounded-full"
              >
                <XCircle size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="text-sm text-white/70 mb-1">User ID</div>
                <div className="font-medium">{selectedRights.user_id}</div>
              </div>
              
              <div>
                <div className="text-sm text-white/70 mb-1">Rights Type</div>
                <div className="font-medium capitalize">{selectedRights.rights_type}</div>
              </div>
              
              <div>
                <div className="text-sm text-white/70 mb-1">Agreement Status</div>
                <div className="font-medium">
                  {selectedRights.agreement_accepted ? (
                    <span className="inline-flex items-center text-green-400">
                      <CheckCircle size={16} className="mr-1" /> Accepted
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-red-400">
                      <XCircle size={16} className="mr-1" /> Not Accepted
                    </span>
                  )}
                </div>
              </div>
              
              {selectedRights.agreement_date && (
                <div>
                  <div className="text-sm text-white/70 mb-1">Agreement Date</div>
                  <div className="font-medium">
                    {new Date(selectedRights.agreement_date).toLocaleString()}
                  </div>
                </div>
              )}
              
              {selectedRights.agreement_version && (
                <div>
                  <div className="text-sm text-white/70 mb-1">Agreement Version</div>
                  <div className="font-medium">{selectedRights.agreement_version}</div>
                </div>
              )}
              
              <div>
                <div className="text-sm text-white/70 mb-1">Created At</div>
                <div className="font-medium">
                  {new Date(selectedRights.created_at).toLocaleString()}
                </div>
              </div>
              
              {selectedRights.territory_restrictions && (
                <div>
                  <div className="text-sm text-white/70 mb-1">Territory Restrictions</div>
                  <div className="font-medium">
                    <pre className="bg-kod-purple-700 p-2 rounded-md text-xs overflow-x-auto">
                      {JSON.stringify(selectedRights.territory_restrictions, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setSelectedRights(null)}
                className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};